// ============================================================================
// ROLE PERMISSIONS HOOKS - Digital Approval System
// ============================================================================
// React hooks for /admin/role-permissions: list + CRUD.
// ============================================================================

import { useState, useEffect, useCallback, useMemo } from 'react';
import { rolePermissionsService } from 'api/services/admin/rolePermissionsService';
import type { PagedResult, PaginationMeta } from 'api/types/common.types';
import type { ID } from 'api/types';
import type {
  RolePermission,
  CreateRolePermissionRequest,
  UpdateRolePermissionRequest,
  RolePermissionFilterParams,
} from 'api/types/admin/rolePermissions.types';

/**
 * Hook to fetch role-permission rows (paginated).
 */
export const useRolePermissions = (params?: RolePermissionFilterParams) => {
  const [data, setData] = useState<PagedResult<RolePermission> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Stabilise params so refetch isn't fired on every render.
  const stableKey = useMemo(() => JSON.stringify(params ?? {}), [params]);

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await rolePermissionsService.list(params);
      setData(result);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stableKey]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const pagination: PaginationMeta | undefined = useMemo(() => {
    if (!data) return undefined;
    return {
      currentPage: data.page,
      pageSize: data.pageSize,
      totalPages: data.totalPages,
      totalItems: data.totalCount,
      hasNextPage: data.hasNextPage ?? (data as any).hasNext ?? false,
      hasPreviousPage: data.hasPreviousPage ?? (data as any).hasPrevious ?? false,
    };
  }, [data]);

  return {
    data,
    items: data?.items ?? [],
    pagination,
    loading,
    error,
    refetch: fetchRows,
  };
};

/**
 * Create a role-permission row. Returns the created entity.
 */
export const useCreateRolePermission = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const create = useCallback(
    async (payload: CreateRolePermissionRequest): Promise<RolePermission> => {
      setLoading(true);
      setError(null);
      try {
        return await rolePermissionsService.create(payload);
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { create, loading, error };
};

/**
 * Update an existing role-permission row.
 */
export const useUpdateRolePermission = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const update = useCallback(
    async (id: ID, payload: UpdateRolePermissionRequest): Promise<RolePermission> => {
      setLoading(true);
      setError(null);
      try {
        return await rolePermissionsService.update(id, payload);
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { update, loading, error };
};

/**
 * Soft-delete a role-permission row. System roles surface a 403 (caller
 * should catch and display the message).
 */
export const useDeleteRolePermission = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const remove = useCallback(async (id: ID): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      await rolePermissionsService.delete(id);
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { remove, loading, error };
};
